import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AddVisit from "./AddVisit";
import PreviousVisitCard from "./PreviousVisitCard";
import { useEffect, useState } from "react";
import { getPreviousVisits, getVisit, getClinicVisitType } from "../../apis/visit";
import { Visit } from "../../interfaces/Visit";
import { format } from "date-fns";
import { useAppDispatch } from "../../hooks";
import { changeVisit } from "../../features/visit/visitSlice";
import { getPatient } from "../../apis/patient";
import LoaderComponent from "../../components/LoaderComponent";
import { Collapse, Modal } from "antd";
import ViewLabOrder from "../laboratory/ViewLabOrder";
import {getLabOrders} from "../../apis/LabOrder";
import { getEpisode } from "../../apis/episode";
import { calculateGestationalAge, formatGplad, parseGplad } from "../../utils/EpisodeMeta";

const VisitTimeline = () => {
  const [previousVisits, setPreviousVisit] = useState<Array<Visit>>([]);
  const [visit, setVisit] = useState<Visit>();
  const [patient, setPatient] = useState<any>(null);
  const [clinicVisitType, setClinicVisitType] = useState<any>(null);
  const [episodeContext, setEpisodeContext] = useState<any>(null);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoader, setLoader] = useState<boolean>(false);
  const [isLabModalOpen, setIsLabModalOpen] = useState(false);
  const [CompletedTests, setCompletedTests] = useState<any[]>([]);
  const [selectedLabOrderId, setSelectedLabOrderId] = useState<number | null>(null);

  useEffect(() => {
    if (params.visitId) {
      fetchVisit(params.visitId);
    }
    if (params.patientId) {
      fetchPatient(params.patientId);
    }
    if (params.clinicVisitTypeId) {
      fetchClinicVisitType(params.clinicVisitTypeId);
    }
    const episodeId = searchParams.get("episodeId");
    if (episodeId) {
      getEpisode(parseInt(episodeId)).then((res: any) => {
        const data = res.data?.data || res.data;
        setEpisodeContext(data);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect to IPD timeline if this is an inpatient visit
  useEffect(() => {
    if (clinicVisitType && clinicVisitType.visitType.type === "ipd") {
      navigate(`/ipd/visits/${params.visitId}`, { replace: true });
    }
  }, [visit, params.visitId, navigate]);

  const fetchPatient = (patientId: any) => {
    getPatient(patientId).then((res: any) => {
      console.log("PATIENT API", res.data);
      setPatient(res.data.data);
    });
  };

  const fetchPreviousVisit = (id: any) => {
    getPreviousVisits(id).then((res: any) => {
      setPreviousVisit(res.data.data);
    });
  };

  const fetchVisit = async (id: any) => {
    setLoader(true);
    await getVisit(id).then(async (res: any) => {
      dispatch(changeVisit(res));
      setVisit(res);
      setLoader(false);
      const patientId = params.patientId || res.patient?.id;
      if (patientId) {
        fetchPatient(patientId);
      } else {
        setPatient(res.patient);
      }
      if (res.parentId) {
        fetchPreviousVisit(res.parentId);
      }
    });
  };

  const fetchClinicVisitType = (id: any) => {
    getClinicVisitType(id).then((res: any) => {
      setClinicVisitType(res.data);
    });
  };

  return (
    <div className="flex flex-col flex-grow">
      <main className="flex flex-col flex-grow bg-[#ECEFFF] rounded-md">
        <div className="p-2">
          <ol className="relative border-l-2 border-primary">
            <li className="mb-4 ml-4">
              <div className="absolute w-4 h-4 border border-white rounded-full bg-primary -left-2 "></div>
              <time className="pb-2 text-sm font-normal leading-none text-active dark:text-gray-500">
                Patient Info
              </time>
              <div className="mb-5 bg-white rounded">
                <Collapse
                  items={[
                    {
                      key: "1",
                      label: (
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {patient?.firstName} {patient?.lastName}({patient?.registrationNumber}) - {visit?.clinicVisitType?.visitType.name
                                ? visit.clinicVisitType.visitType.name
                                : clinicVisitType?.name}
                        </span>
                      ),
                      children: (
                        <>
                        <div className="space-y-0">
                          {(visit?.episode || episodeContext) && (() => {
                            const ep = visit?.episode ?? episodeContext;
                            const meta = ep.meta ?? {};
                            const fields: any[] = ep.episodeType?.fields ?? [];
                            const populated = fields.filter((f: any) => {
                              const v = meta[f.key];
                              return v !== undefined && v !== null && v !== "";
                            });
                            return (
                              <div className="px-4 pt-3 pb-2 bg-indigo-50 border-b border-indigo-100 rounded-t">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                    {ep.episodeType?.name ?? "Episode"}
                                  </span>
                                  <span className="text-xs text-gray-400">Episode #{ep.id}</span>
                                </div>
                                {populated.length > 0 ? (
                                  <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
                                    {populated.map((f: any) => {
                                      const raw = meta[f.key];
                                      let display: React.ReactNode;
                                      if (f.type === "lmp") {
                                        const ga = calculateGestationalAge(raw);
                                        display = (
                                          <span className="flex items-center gap-1.5">
                                            <span>{raw}</span>
                                            {ga && (
                                              <span className="inline-flex items-center rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100">
                                                {ga.label} GA
                                              </span>
                                            )}
                                          </span>
                                        );
                                      } else if (f.type === "gplad") {
                                        display = <span className="font-mono text-xs">{formatGplad(parseGplad(raw))}</span>;
                                      } else if (f.type === "boolean") {
                                        display = <span className={raw ? "text-green-700 font-medium" : "text-gray-500"}>{raw ? "Yes" : "No"}</span>;
                                      } else {
                                        display = <span>{String(raw)}</span>;
                                      }
                                      return (
                                        <div key={f.key}>
                                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400">{f.label}</dt>
                                          <dd className="mt-0.5 text-sm text-gray-800">{display}</dd>
                                        </div>
                                      );
                                    })}
                                  </dl>
                                ) : (
                                  <p className="text-xs text-gray-400">No intake details recorded.</p>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="grid grid-cols-3 gap-4 p-4">
                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">
                              Patient Name:{" "}
                            </span>
                            <span className="ml-2 text-gray-800 capitalize">
                              {patient?.firstName} {patient?.lastName} ({patient?.registrationNumber})
                            </span>
                          </div>
                          
                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">Phone: </span>
                            <span className="ml-2 text-gray-800">{patient?.phone}</span>
                          </div>

                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">Patient Habits: </span>
                            <span className="ml-2 text-gray-800">
                              {(() => {
                                const habits = [];
                                if (patient?.isAlcoholic) habits.push("Alcohol");
                                if (patient?.isSmoking) habits.push("Smoking");
                                if (patient?.isBloodDisorder) habits.push("Blood Disorder");
                                if (patient?.isBloodPressure) habits.push("Blood Pressure");
                                if (patient?.isDiabetic) habits.push("Diabetic");
                                return habits.length > 0 ? habits.join(", ") : "No habits recorded";
                              })()}
                            </span>
                          </div>

                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">Previous Surgeries: </span>
                            <span className="ml-2 text-gray-800">
                              {patient?.historyData?.previousSurgeries?.map((s: any) => s.label).join(", ") || "-"}
                            </span>
                          </div>

                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">Allergies: </span>
                            <span className="ml-2 text-gray-800">
                              {patient?.historyData?.allergies?.map((a: any) => a.label).join(", ") || "-"}
                            </span>
                          </div>

                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">Previous Diseases: </span>
                            <span className="ml-2 text-gray-800">
                              {patient?.historyData?.previousDiseases?.map((d: any) => d.label).join(", ") || "-"}
                            </span>
                          </div>

                          <div className="flex w-full">
                            <span className="font-medium text-gray-900">Ongoing Medicines: </span>
                            <span className="ml-2 text-gray-800">
                              {patient?.historyData?.ongoingmedicines?.map((d: any) => d.label).join(", ") || "-"}
                            </span>
                          </div>

                          <div className="flex flex-wrap text-sm">
                            <span className="font-medium">Lab Tests: </span>
                            {patient?.labOrders?.length > 0 ? (
                              <span className="ml-2 text-gray-800">
                                {patient.labOrders.map((order: any, index: number) => (
                                  <span key={order.id}>
                                    <span
                                      className="text-primary underline cursor-pointer"
                                      onClick={() => {
                                        setSelectedLabOrderId(order.id);
                                        setIsLabModalOpen(true);
                                      }}
                                    >
                                      {order.labTest?.name}
                                    </span>
                                    {index < patient.labOrders.length - 1 && ", "}
                                  </span>
                                ))}
                              </span>
                            ) : (
                              <span className="ml-2 text-gray-500">No lab tests</span>
                            )}
                          </div>
                        </div>
                        </>
                      ),
                    },
                  ]}
                />
              </div>
            </li>
            <li className="mb-4 ml-4">
              <div className="absolute w-4 h-4 bg-primary rounded-full mt-1.5 -left-2 border border-white "></div>
              <time className="mb-1 text-sm font-normal leading-none text-active dark:text-gray-500">
                <span>
                  {visit &&
                    visit.date &&
                    format(new Date(visit?.date), "yyyy-MM-dd")}
                </span>
              </time>
              {isLoader ? <LoaderComponent /> : <AddVisit />}
            </li>

            {previousVisits &&
              previousVisits.length > 0 &&
              previousVisits.map((i, index: number) => (
                <li className="ml-4" key={index}>
                  <PreviousVisitCard key={i.id} previousVisit={i} />
                </li>
              ))}
          </ol>
        </div>
      </main>

      <Modal
        open={isLabModalOpen}
        footer={null}
        onCancel={() => setIsLabModalOpen(false)}
        width={1100}
        title="Lab Test Reports"
      >
        {patient?.id && (
          <ViewLabOrder
            isModal={true}
            // patientId={patient.id}
            orderId={selectedLabOrderId}
            onClose={() => setIsLabModalOpen(false)}
          />
        )}

      </Modal>

    </div>
  );
};

export default VisitTimeline;

